import React, { createContext, FC, useCallback, useContext, useMemo, useState } from "react";
import { GeometryType } from "@evergis/api";
import { UploaderItemProps } from "@evergis/ui";

import { useSysAttributes } from "../../../hooks/attributes/useSysAttributes";
import { useResourceMeta } from "../../../hooks/useResourceMeta";
import { getResourceName } from "../../../utils/resourceName";
import { FileData, FileLoader } from "./hooks/useFileLoader";
import { useImportAttributes } from "./hooks/useImportAttributes";
import { useSpatialReference } from "./hooks/useSpatialReference";
import { getLayerDefinitions } from "./utils/getLayerDefinitions";
import { DataSchema, ImportAttribute, ImportContextType, ImportObject, ImportTargetType, ImportType } from "./types";
import { SchemaParsed } from "../../../evergis/core/import/parseSchema";

const ImportContext = createContext<ImportContextType>({ step: 0 });

// eslint-disable-next-line max-lines-per-function
export const ImportProvider: FC<{ data: ImportContextType }> = ({ data, children }) => {
  const { step: stepProp } = data;
  const [step, setStep] = useState(stepProp);
  const [file, setFile] = useState<FileData & FileLoader>();
  const [fileType, setFileType] = useState("");
  const [importedFiles, setImportedFiles] = useState<UploaderItemProps[]>([]);
  const [importType, setImportType] = useState<ImportType>();
  const [dataSchema, setDataSchema] = useState<DataSchema>();
  const [objects, setObjects] = useState<ImportObject[]>();
  const [geometryType, setGeometryType] = useState<GeometryType>();
  const [target, setTarget] = useState<ImportTargetType>(ImportTargetType.Catalog);

  const nextStep = useCallback(() => {
    setStep(step + 1);
  }, [step]);

  const prevStep = useCallback(() => {
    setStep(step > 0 ? step - 1 : step);
  }, [step]);

  const initialMeta = useMemo(
    () => ({
      alias: "",
      name: getResourceName(),
      description: "",
      preview: {
        src: "",
        file: null,
      },
    }),
    [],
  );

  const [meta, updateMeta] = useResourceMeta(initialMeta);

  const importAttributesParams = useMemo(
    () =>
      importType === ImportType.KML && geometryType
        ? {
            layerDefinitions: getLayerDefinitions(dataSchema as SchemaParsed[], geometryType),
          }
        : dataSchema
        ? {
            attributes: (dataSchema as SchemaParsed).schemaAttributes,
            geometryType: (dataSchema as SchemaParsed).geometryType,
            geometryAttribute: (dataSchema as SchemaParsed).geometryAttribute,
          }
        : {
            attributes: void 0,
            geometryType: null,
            geometryAttribute: null,
          },
    [dataSchema, importType, geometryType],
  );

  const [initialAttributes] = useImportAttributes(importAttributesParams);
  const [attributes, setAttributes] = useState<ImportAttribute[]>(initialAttributes);
  const [sysAttributes, { addSysAttributes, deleteSysAttribute, updateSysAttribute }] =
    useSysAttributes<ImportAttribute>();

  const [{ srFields, lonLatInOneField, geometrySpecified }, { updateSrField, setLonLatInOneField }] =
    useSpatialReference(dataSchema, attributes || initialAttributes);

  const value: ImportContextType = {
    step,
    nextStep,
    prevStep,
    file,
    fileType,
    target,
    setTarget,
    setFileType,
    setImportType,
    setFile,
    importedFiles,
    setImportedFiles,
    dataSchema,
    setDataSchema,
    attributes: attributes || initialAttributes,
    setAttributes,
    objects,
    setObjects,
    srFields,
    updateSrField,
    lonLatInOneField,
    setLonLatInOneField,
    geometrySpecified,
    meta,
    updateMeta,
    importType,
    geometryType,
    setGeometryType,
    sysAttributes,
    addSysAttributes,
    deleteSysAttribute,
    updateSysAttribute,
  };

  return <ImportContext.Provider value={value}>{children}</ImportContext.Provider>;
};

export const useImportContext = (): ImportContextType => useContext(ImportContext);
